/**
 * SMS opt-in consent constants (issue #342).
 *
 * The Notifications page renders a CTIA / Twilio A2P 10DLC-compliant consent
 * paragraph beside the SMS consent checkbox. The paragraph prose itself lives
 * as JSX in `NotificationsSettings.tsx` (it carries inline bold + two links);
 * this module holds the pieces that need to be referenced as values.
 *
 * `CONSENT_COPY_VERSION` must stay in lockstep with the server-owned
 * `CONSENT_COPY_VERSION` in `backend/src/services/consent-copy.ts` — the
 * frontend renders the copy, the backend stamps the version into the
 * `sms_opt_in` audit record. Bump both together whenever the paragraph
 * changes. Format: `vN-YYYY-MM`.
 *
 * v2-2026-09 (djin-cloud#743): the program name is the TENANT's name, not a
 * hard-coded "DJIN EMS Project Alerts" — POD's portal says "Power on Demand
 * Project Alerts", DJIN's says "DJIN EMS Project Alerts". Owner ruling, taken
 * twice, the second time with the legal risk shown: the linked SMS Privacy
 * Policy and Terms still name DJIN EMS as the data-processing party and are
 * deliberately UNCHANGED — an accepted risk, recorded in the vault. Because the
 * copy now varies per tenant, the version alone no longer reconstructs what a
 * user agreed to; the `sms_opt_in` audit row also carries
 * `consent_program_name`.
 */
export const CONSENT_COPY_VERSION = 'v2-2026-09';

/**
 * The SMS program name shown in the consent paragraph. Mirrors
 * `smsProgramName` in `backend/src/services/consent-copy.ts` — keep the two
 * byte-identical; the backend stamps its result into the opt-in audit row.
 */
export function smsProgramName(tenantName: string): string {
  return `${tenantName} Project Alerts`;
}

/**
 * Public SMS legal pages. Relative paths so each tenant (pod.djinems.com /
 * cloud.djinems.com) serves its own copy. The pages are static HTML served
 * by the cloud-ui nginx `location /legal/` block (sources: docs/legal/).
 * Paths must match the deployed filenames and the pages' embedded og:url.
 */
export const SMS_PRIVACY_URL = '/legal/sms-privacy-policy';
export const SMS_TERMS_URL = '/legal/sms-terms';
